import React, { useMemo, useState } from 'react';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import ToolkitLocalFileStep from './toolkit/ToolkitLocalFileStep';
import ToolkitCompareResults from './toolkit/ToolkitCompareResults';
import { parsePepGroupXml, parseOperaBlockXml, matchGroups } from '../utils/pepBalancing';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › PEP Group Master — ported from the desktop's PEP Balancing › GM – Group Master. The PEP
// group master file against the Opera v5 Block Info export, matched on group / block code: in both (and whether
// status, rate plan, dates or name differ), in PEP only, in Opera only. Both files are read in the browser.

const COLUMNS = [
    { key: 'code', label: 'Code' },
    { key: 'pepName', label: 'PEP name' },
    { key: 'operaName', label: 'Opera name' },
    { key: 'dates', label: 'Dates PEP / Opera' },
    { key: 'statuses', label: 'Status PEP / Opera' },
    { key: 'rates', label: 'Rate plan PEP / Opera' },
    { key: 'pepReservations', label: 'PEP reservations' },
    { key: 'differences', label: 'Differences' },
];
const STATUSES = [{ value: 'match', label: 'In both' }, { value: 'partial', label: 'In both — different' },
    { value: 'missing', label: 'PEP only' }, { value: 'cloud-only', label: 'Opera only' }];

const row = (status, statusText, p, o, diffs = []) => ({
    status, statusText, code: (p || o).group_code, pepName: p?.group_name || '', operaName: o?.group_name || '',
    dates: `${p ? `${p.group_begin_date}–${p.group_end_date}` : '—'} / ${o ? `${o.group_begin_date}–${o.group_end_date}` : '—'}`,
    statuses: `${p?.status || '—'} / ${o?.status || '—'}`, rates: `${p?.rate_plan || '—'} / ${o?.rate_plan || '—'}`,
    pepReservations: p ? p.reservation_count : '', differences: diffs.join(', '),
});

function ToolkitPepGroupMasterPage() {
    const toolkit = useToolkitProject();
    const [pep, setPep] = useState(null);
    const [opera, setOpera] = useState(null);

    const result = useMemo(() => {
        if (!pep || !opera) return null;
        const { matched, pepOnly, operaOnly } = matchGroups(pep, opera);
        const rows = [
            ...matched.map((m) => row(m.diffs.length ? 'partial' : 'match', m.diffs.length ? 'In both — different' : 'In both', m.pep, m.opera, m.diffs)),
            ...pepOnly.map((p) => row('missing', 'PEP only', p, null)),
            ...operaOnly.map((o) => row('cloud-only', 'Opera only', null, o)),
        ];
        return { rows, summary: [
            { label: 'PEP groups', value: pep.length }, { label: 'Opera blocks', value: opera.length },
            { label: 'In both', value: matched.filter((m) => !m.diffs.length).length, tone: 'ok' },
            { label: 'In both — different', value: matched.filter((m) => m.diffs.length).length, tone: 'warn' },
            { label: 'PEP only', value: pepOnly.length, tone: pepOnly.length ? 'fail' : 'ok' },
            { label: 'Opera only', value: operaOnly.length, tone: operaOnly.length ? 'warn' : 'ok' },
        ] };
    }, [pep, opera]);

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="PEP Group Master"
                subtitle="Compare the PEP group master file with the Opera v5 Block Info export, group by group." />
            <ToolkitLocalFileStep number={1} title="PEP group master file" label="Choose the PEP group XML" accept=".xml,.txt"
                onFile={async (text) => { const g = await parsePepGroupXml(text); setPep(g); return `${g.length} group(s)`; }} />
            <ToolkitLocalFileStep number={2} title="Opera v5 Block Info export" label="Choose the Block Info XML" accept=".xml,.txt,.rep"
                onFile={async (text) => { const b = await parseOperaBlockXml(text); setOpera(b); return `${b.length} block(s)`; }} />
            {result && <ToolkitCompareResults summary={result.summary} rows={result.rows} columns={COLUMNS} statuses={STATUSES} exportName="PEP_Group_Master" />}
        </div>
    );
}

export default ToolkitPepGroupMasterPage;
